<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-envelope-o"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">{{ isEdit ? 'Edit Whitelist Entry' : 'Add Whitelist Entry' }}</div>
          <div class="panel-header__sub">{{ isEdit ? 'Update whitelisted email and contact details' : 'Register a new email address in the whitelist' }}</div>
        </div>
      </div>

      <div class="form-body">

        <!-- Section: Company & Contact -->
        <div class="form-section">
          <div class="section-header">
            <i class="fa fa-building section-header__icon"></i>
            <span>Company &amp; Contact</span>
          </div>
          <div class="field-grid">
            <div class="field-group" :class="{ 'field-group--error': errors['company_name'] }">
              <label class="field-label">Company <span class="req">*</span></label>
              <input v-model="company_name" type="text" class="field-input" placeholder="Enter company name" />
              <span class="field-error" v-if="errors['company_name']">{{ errors['company_name'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['contact_name'] }">
              <label class="field-label">Contact Person</label>
              <input v-model="contact_name" type="text" class="field-input" placeholder="Enter contact name" />
              <span class="field-error" v-if="errors['contact_name']">{{ errors['contact_name'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['email'] }">
              <label class="field-label">Email <span class="req">*</span></label>
              <input v-model="email" type="email" class="field-input" placeholder="Enter email address" />
              <span class="field-error" v-if="errors['email']">{{ errors['email'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['contact_phone_no'] }">
              <label class="field-label">Contact Phone</label>
              <input v-model="contact_phone_no" type="text" v-mask="'(###)-###-####'" class="field-input" placeholder="(###)-###-####" />
              <span class="field-error" v-if="errors['contact_phone_no']">{{ errors['contact_phone_no'][0] }}</span>
            </div>
          </div>
        </div>

        <!-- Section: Address -->
        <div class="form-section">
          <div class="section-header">
            <i class="fa fa-map-marker section-header__icon"></i>
            <span>Address</span>
          </div>
          <div class="field-grid">
            <div class="field-group" :class="{ 'field-group--error': errors['address1'] }">
              <label class="field-label">Address Line 1</label>
              <input v-model="address1" type="text" class="field-input" placeholder="Street address" />
              <span class="field-error" v-if="errors['address1']">{{ errors['address1'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['address2'] }">
              <label class="field-label">Address Line 2</label>
              <input v-model="address2" type="text" class="field-input" placeholder="Apt, suite, etc." />
              <span class="field-error" v-if="errors['address2']">{{ errors['address2'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['city'] }">
              <label class="field-label">City</label>
              <input v-model="city" type="text" class="field-input" placeholder="City" />
              <span class="field-error" v-if="errors['city']">{{ errors['city'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['state'] }">
              <label class="field-label">State</label>
              <input v-model="state" type="text" class="field-input" placeholder="State" />
              <span class="field-error" v-if="errors['state']">{{ errors['state'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['zipcode'] }">
              <label class="field-label">Zipcode</label>
              <input v-model="zipcode" type="text" class="field-input" placeholder="Zipcode" />
              <span class="field-error" v-if="errors['zipcode']">{{ errors['zipcode'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['country'] }">
              <label class="field-label">Country</label>
              <input v-model="country" type="text" class="field-input" placeholder="Country" />
              <span class="field-error" v-if="errors['country']">{{ errors['country'][0] }}</span>
            </div>
          </div>

          <!-- Google Map -->
          <div class="map-wrap">
            <label class="field-label">Select Address on Map</label>
            <div class="map-box">
              <keep-alive>
                <GoogleMap
                  ref="clientmap"
                  @getFullAddress="setFullAddress"
                  @dragPointer="setLocation"
                  @onSearchClick="setLocation"
                />
              </keep-alive>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Floating save bar -->
    <div class="save-bar">
      <button class="save-bar__back" @click="goBack()">
        <i class="fa fa-arrow-left"></i> Back
      </button>
      <div class="save-bar__right">
        <template v-if="!isEdit">
          <button class="save-bar__secondary" ref="kt_save_and_add_service"
            @click="store('kt_save_and_add_service', 'SAA')">
            <i class="fa fa-plus"></i> Save &amp; Add Another
          </button>
          <button class="save-bar__primary" ref="kt_save_service"
            @click="store('kt_save_service', 'S')">
            <i class="fa fa-check"></i> Save
          </button>
        </template>
        <button v-else class="save-bar__primary" ref="kt_update_service"
          @click="update('kt_update_service')">
          <i class="fa fa-check"></i> Update
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  CREATE_WHITELIST_EMAIL,
  FETCH_WHITELISTEMAIL_DETAILS,
} from "@/core/services/store/actions.type";
import GoogleMap from "@/components/GoogleMap.vue";

export default {
  components: { GoogleMap },
  computed: {
    ...mapGetters(["currentUser"]),
  },
  data() {
    return {
      errors: [],
      isEdit: false,
      id: 0,
      email: "",
      company_name: "",
      contact_name: "",
      contact_phone_no: "",
      address1: "",
      address2: "",
      city: "",
      zipcode: "",
      country: "",
      state: "",
      lat: "",
      lng: "",
      breadCrumbs: [
        { id: 1, title: "Dashboard",       route: "client.dashboard" },
        { id: 2, title: "WhiteList Email", route: "client.whitelistemail" },
        { id: 3, title: "Create New",      route: "" },
      ],
    };
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.isEdit = this.$route.name === "client.whitelist.edit";
    if (this.isEdit) this.fetchEmailDetails();
  },
  methods: {
    clearForm() {
      this.id = 0; this.email = ""; this.contact_name = ""; this.company_name = "";
      this.contact_phone_no = ""; this.address1 = ""; this.address2 = "";
      this.zipcode = ""; this.country = ""; this.state = ""; this.city = "";
      this.lat = ""; this.lng = "";
    },
    loadingButton(ref) {
      const btn = this.$refs[ref];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");
    },
    closeBtnLoad(ref) {
      const btn = this.$refs[ref];
      btn.classList.remove("spinner", "spinner-light", "spinner-right");
      btn.disabled = false;
    },
    payload() {
      return {
        id: this.id, email: this.email, contact_name: this.contact_name,
        company_name: this.company_name, contact_phone_no: this.contact_phone_no,
        address1: this.address1, address2: this.address2, zipcode: this.zipcode,
        country: this.country, state: this.state, city: this.city,
        lat: this.lat, lng: this.lng,
      };
    },
    setLocation(location) {
      this.lng      = location.lng;
      this.lat      = location.lat;
      this.city     = location.city.long_name;
      this.address1 = location.formatted_address;
      this.zipcode  = location.postal_code.long_name;
      this.country  = location.country.long_name;
      this.state    = location.state.long_name;
    },
    setFullAddress(data) { this.address1 = data; },
    update(ref) {
      this.loadingButton(ref);
      this.$store.dispatch(CREATE_WHITELIST_EMAIL, this.payload())
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          this.$router.push({ name: "client.whitelistemail" });
        })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
    store(ref, saveType) {
      this.loadingButton(ref);
      this.$store.dispatch(CREATE_WHITELIST_EMAIL, this.payload())
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          if (saveType === "S") {
            this.$router.push({ name: "client.whitelistemail" });
          } else {
            this.clearForm();
          }
        })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
    goBack() {
      this.$router.push({ name: "client.whitelistemail", params: { client_slug: this.$route.params.client_slug } });
    },
    fetchEmailDetails() {
      this.breadCrumbs[2].title = "Edit";
      this.$store.dispatch(FETCH_WHITELISTEMAIL_DETAILS, this.$route.params.id)
        .then(data => {
          const d = data.data;
          this.id = d.id; this.email = d.email;
          this.contact_name = d.contact_name; this.company_name = d.company_name;
          this.contact_phone_no = d.contact_phone_no;
          this.address1 = d.address1; this.address2 = d.address2;
          this.zipcode = d.zipcode; this.country = d.country;
          this.state = d.state; this.city = d.city;
          this.lat = d.lat; this.lng = d.lng;
          this.$refs.clientmap.defaultMarkerPosition(Number(d.lat), Number(d.lng));
        });
    },
  },
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 12px 12px 96px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px; height: 48px; border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 18px; flex-shrink: 0;
  }
  &__text { flex: 1; }
  &__title { font-size: 16px; font-weight: 700; color: $dark; line-height: 1.2; }
  &__sub   { font-size: 12px; color: #7a8499; margin-top: 3px; }
}

/* ── form body ── */
.form-body { padding: 8px 0; }

/* ── section ── */
.form-section {
  padding: 20px 28px 24px;
  & + & { border-top: 1px solid $border; }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: $dark;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;

  &__icon { font-size: 13px; color: $blue; }
}

/* ── field grid ── */
.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
}

.field-group {
  display: flex; flex-direction: column; gap: 5px;
  &--error .field-input { border-color: #b42318; }
}

.field-label {
  font-size: 12px; font-weight: 600;
  color: #5a6278; font-family: $font;
  .req { color: #b42318; }
}

.field-input {
  height: 38px;
  border: 1.5px solid $border; border-radius: 8px;
  padding: 0 12px; font-size: 13px;
  color: $dark; font-family: $font;
  outline: none; background: #fff; width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus { border-color: $blue; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
  &::placeholder { color: #b0bac9; }
}

.field-error { font-size: 11px; color: #b42318; }

/* ── map ── */
.map-wrap {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.map-box {
  border: 1.5px solid $border;
  border-radius: 12px;
  overflow: hidden;
  min-height: 320px;
}

/* ── floating save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13,27,62,0.3);
  z-index: 100;

  &__right { display: flex; align-items: center; gap: 10px; }

  &__back {
    display: inline-flex; align-items: center; gap: 7px;
    background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.75);
    border: 1px solid rgba(255,255,255,0.15); border-radius: 9px;
    padding: 8px 16px; font-size: 13px; font-weight: 600;
    font-family: $font; cursor: pointer; transition: background 0.15s;
    &:hover { background: rgba(255,255,255,0.14); }
  }

  &__secondary {
    display: inline-flex; align-items: center; gap: 7px;
    background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.8);
    border: 1px solid rgba(255,255,255,0.2); border-radius: 9px;
    padding: 8px 18px; font-size: 13px; font-weight: 600;
    font-family: $font; cursor: pointer; transition: background 0.15s;
    &:hover { background: rgba(255,255,255,0.14); }
    &:disabled { opacity: 0.5; cursor: default; }
  }

  &__primary {
    display: inline-flex; align-items: center; gap: 7px;
    background: $blue; color: #fff; border: none; border-radius: 9px;
    padding: 8px 22px; font-size: 13px; font-weight: 600;
    font-family: $font; cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;
    &:hover { background: #2d85bb; box-shadow: 0 4px 14px rgba(56,154,212,0.4); }
    &:disabled { opacity: 0.6; cursor: default; box-shadow: none; }
  }
}
</style>
